import { useEffect, useState } from 'react';
import { Copy, KeyRound, RotateCcw } from 'lucide-react';
import { formatDateTime } from '@nu/shared';
import { api } from '../api/client.js';
import { problem } from '../modules/bookings/util.js';

// A strong password that's easy to read out over the phone: four groups of
// four from letters and digits that can't be mistaken for each other (no 0/O,
// 1/l/I), e.g. "K7mq-Xr4t-Bw9h-Jd3p". Made on this computer; never sent
// anywhere but to be saved (as a hash).
const CHARS = 'ABCDEFGHJKMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789';
export function generatePassword() {
  const bytes = new Uint32Array(16);
  crypto.getRandomValues(bytes);
  const s = [...bytes].map((b) => CHARS[b % CHARS.length]).join('');
  return s.match(/.{4}/g).join('-');
}

// Platform > Network Plus page (/networkplus/jobs): the link to give them, the
// password (master admins; at least 10 characters; only a hash is kept;
// changing or resetting it signs everyone out), and switching the page off.
export default function PartnerAccess() {
  const [st, setSt] = useState(null);
  const [pw, setPw] = useState('');
  const [pw2, setPw2] = useState('');
  const [shown, setShown] = useState(false); // the password in plain text (a generated one)
  const [fresh, setFresh] = useState(null); // just saved: shown once to send on
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  const link = `${window.location.origin}/networkplus/jobs`;
  useEffect(() => {
    api('/partner-access').then(setSt, (e) => setMsg({ tone: 'bad', text: problem(e) }));
  }, []);
  const put = async (body, done) => {
    setBusy(true);
    setMsg(null);
    try {
      setSt(await api('/partner-access', { method: 'PUT', body }));
      setMsg({ tone: 'ok', text: done });
      if (body.password) setFresh(body.password);
      setPw('');
      setPw2('');
      setShown(false);
      return true;
    } catch (e) {
      setMsg({ tone: 'bad', text: problem(e) });
      return false;
    } finally {
      setBusy(false);
    }
  };
  const copy = (text, what) => navigator.clipboard?.writeText(text).then(() => setMsg({ tone: 'ok', text: `${what} copied.` }), () => {});
  const mismatch = pw2 !== '' && pw !== pw2;
  const submit = (e) => {
    e.preventDefault();
    if (mismatch || pw.length < 10) return;
    put({ password: pw }, st?.set ? 'Password changed. Anyone signed in with the old one has to enter the new one.' : 'Password set: the page is on.');
  };
  const generate = () => {
    const p = generatePassword();
    setPw(p);
    setPw2(p);
    setShown(true);
    setFresh(null);
  };
  // Reset: a new generated password, saved at once (everyone signed out).
  const reset = () => put({ password: generatePassword() }, 'Password reset. Anyone signed in with the old one has been signed out.');

  return (
    <section className="card stack partner-access">
      <div className="page-head"><div><h1>Network Plus page</h1></div></div>
      <p className="small">A page where Network Plus see all their survey jobs, live, with Excel and PDF exports, behind a password. They don’t sign in with Microsoft. They see the survey answers, gang and planned dates; not who surveyed, where the phone was, or photos.</p>
      <div className="partner-link">
        <code>{link}</code>
        <button type="button" className="btn with-icon" onClick={() => copy(link, 'Link')}><Copy size={16} aria-hidden="true" /> Copy link</button>
        <a className="btn" href={link} target="_blank" rel="noreferrer">Open</a>
      </div>
      {st && !st.set && <p className="small warn-text"><strong>No password yet: set one below to switch the page on.</strong></p>}
      {st && st.set && (
        <p className="small">
          The page is <strong className={st.enabled ? 'ok-text' : 'warn-text'}>{st.enabled ? 'on' : 'off'}</strong>.
          {' '}Password set {formatDateTime(st.setAt)} by {st.setBy}.
          {' '}{st.lastOpenedAt ? `Last opened ${formatDateTime(st.lastOpenedAt)} (${st.opens} time${st.opens === 1 ? '' : 's'}).` : 'Not opened yet.'}
        </p>
      )}

      {fresh && (
        <div className="partner-fresh" role="status">
          <span className="small">The new password. Copy it now: it can’t be shown again once you leave this page.</span>
          <div className="partner-link">
            <code className="partner-pw">{fresh}</code>
            <button type="button" className="btn primary with-icon" onClick={() => copy(fresh, 'Password')}><Copy size={16} aria-hidden="true" /> Copy password</button>
            <button type="button" className="link-btn" onClick={() => setFresh(null)}>Done</button>
          </div>
        </div>
      )}

      <form className="stack" onSubmit={submit}>
        <div className="fld-row">
          <label className="fld">
            <span>{st?.set ? 'New password' : 'Password'} <span className="small muted">(at least 10 characters)</span></span>
            <input type={shown ? 'text' : 'password'} autoComplete="new-password" value={pw} onChange={(e) => { setPw(e.target.value); setShown(false); }} spellCheck={false} />
          </label>
          <label className="fld">
            <span>Type it again</span>
            <input type={shown ? 'text' : 'password'} autoComplete="new-password" value={pw2} onChange={(e) => setPw2(e.target.value)} spellCheck={false} />
          </label>
        </div>
        {mismatch && <p className="small warn-text">The two don’t match.</p>}
        <div className="actions">
          <button type="button" className="btn with-icon" disabled={busy} onClick={generate}><KeyRound size={16} aria-hidden="true" /> Generate password</button>
          {shown && pw && <button type="button" className="btn with-icon" onClick={() => copy(pw, 'Password')}><Copy size={16} aria-hidden="true" /> Copy</button>}
          <button type="submit" className="btn primary" disabled={busy || pw.length < 10 || pw !== pw2}>{st?.set ? 'Change password' : 'Set password'}</button>
        </div>
        <div className="actions">
          {st?.set && (
            <button type="button" className="btn with-icon" disabled={busy} onClick={reset}><RotateCcw size={16} aria-hidden="true" /> Reset password</button>
          )}
          {st?.set && (
            <button type="button" className="btn" disabled={busy} onClick={() => put({ enabled: !st.enabled }, st.enabled ? 'Page switched off.' : 'Page switched on.')}>
              {st.enabled ? 'Switch page off' : 'Switch page on'}
            </button>
          )}
        </div>
      </form>
      <p className="small muted">Reset makes a new password at once and signs out anyone using the old one. Send Network Plus the link and the password separately (for example the link by email and the password by phone).</p>
      {msg && <p className={`notice-line notice-${msg.tone}`} role="status">{msg.text}</p>}
    </section>
  );
}
